import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Briefcase, Users, UsersThree, CurrencyDollar, TrendUp, Clock, WarningCircle, UserPlus, Warning, CheckCircle } from "@phosphor-icons/react";

export default function Dashboard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });

  const complete = useMutation({
    mutationFn: async (project) => (await api.patch(`/projects/${project.project_id}`, {
      title: project.title, client_id: project.client_id, interpreter_id: project.interpreter_id || null,
      project_type: project.project_type, language: project.language, scheduled_at: project.scheduled_at,
      duration_hours: project.duration_hours, location: project.location,
      client_rate: project.client_rate, interpreter_rate: project.interpreter_rate,
      status: "completed",
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Marked complete — cleared from queue");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to complete"),
  });

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Overview"
        title="Operations Dashboard"
        description="Live view of active work, pipeline, and revenue across all francophone assignments."
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <KPI icon={<Briefcase size={18} weight="duotone" />} label="Active projects" value={data?.active_projects ?? 0} tone="blue" testid="kpi-active" />
        <KPI icon={<Users size={18} weight="duotone" />} label="Clients" value={data?.total_clients ?? 0} tone="slate" testid="kpi-clients" />
        <KPI icon={<UsersThree size={18} weight="duotone" />} label="Interpreters" value={data?.total_interpreters ?? 0} tone="slate" testid="kpi-interpreters" />
        <KPI icon={<CurrencyDollar size={18} weight="duotone" />} label="Outstanding" value={fmtMoney(data?.outstanding || 0)} tone="amber" testid="kpi-outstanding" />
      </div>

      {/* Action items row — only shown when there's something needing attention */}
      {(data?.at_risk?.length > 0 || data?.pending_apps > 0 || data?.w9_missing > 0 || data?.signed_unpaid_count > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="action-items">
          {data.signed_unpaid_count > 0 && (
            <ActionCard
              tone="amber"
              icon={<CurrencyDollar size={18} weight="duotone" />}
              title={`${data.signed_unpaid_count} signed but unpaid`}
              subtitle="Client signed the contract — deposit not yet received"
              to="/projects"
              testid="action-signed-unpaid"
            >
              <ul className="mt-2 space-y-1">
                {(data.signed_unpaid || []).slice(0, 3).map((c) => (
                  <li key={c.contract_id} className="text-xs truncate">
                    · <span className="font-medium">{c.project_title || c.contract_number}</span>
                    <span className="text-slate-400"> — signed {new Date(c.signed_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            </ActionCard>
          )}
          {data.at_risk?.length > 0 && (
            <ActionCard
              tone="red"
              icon={<WarningCircle size={18} weight="duotone" />}
              title={`${data.at_risk.length} booking${data.at_risk.length > 1 ? "s" : ""} at risk`}
              subtitle="Payment window closing in < 24h"
              to="/projects"
              testid="action-at-risk"
            >
              <ul className="mt-2 space-y-1">
                {data.at_risk.slice(0, 3).map((p) => (
                  <li key={p.project_id} className="text-xs truncate">
                    · <span className="font-medium">{p.title}</span> — {new Date(p.payment_deadline).toLocaleString()}
                  </li>
                ))}
              </ul>
            </ActionCard>
          )}
          {data.pending_apps > 0 && (
            <ActionCard
              tone="amber"
              icon={<UserPlus size={18} weight="duotone" />}
              title={`${data.pending_apps} pending application${data.pending_apps > 1 ? "s" : ""}`}
              subtitle="New interpreters awaiting your review"
              to="/pending-applications"
              testid="action-pending-apps"
            />
          )}
          {data.w9_missing > 0 && (
            <ActionCard
              tone="amber"
              icon={<Warning size={18} weight="duotone" />}
              title={`${data.w9_missing} interpreter${data.w9_missing > 1 ? "s" : ""} missing W-9`}
              subtitle="Cannot receive assignments until on file"
              to="/interpreters"
              testid="action-w9-missing"
            >
              <ul className="mt-2 space-y-1">
                {(data.w9_missing_list || []).slice(0, 3).map((i) => (
                  <li key={i.interpreter_id} className="text-xs truncate">· {i.full_name}</li>
                ))}
              </ul>
            </ActionCard>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 card-flat">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-slate-900">Upcoming assignments</div>
              <div className="text-xs text-slate-500 mt-0.5">Next 5 pending or confirmed projects</div>
            </div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Pipeline</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Project</th>
                  <th className="text-left px-3 py-3 font-semibold">Type</th>
                  <th className="text-left px-3 py-3 font-semibold">Scheduled</th>
                  <th className="text-left px-3 py-3 font-semibold">Status</th>
                  <th className="text-right px-3 py-3 font-semibold">Client rate</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
                ) : (data?.upcoming || []).length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No upcoming projects yet.</td></tr>
                ) : (
                  data.upcoming.map((p) => (
                    <tr key={p.project_id} className="data-row border-t border-slate-100" data-testid={`upcoming-row-${p.project_id}`}>
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-900">{p.title}</div>
                        <div className="text-xs text-slate-500 font-mono">{p.language || "—"}</div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{p.project_type}</td>
                      <td className="px-3 py-3 text-slate-600">{fmtDate(p.scheduled_at)}</td>
                      <td className="px-3 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-3 py-3 text-right font-mono">{fmtMoney(p.client_rate)}</td>
                      <td className="px-5 py-3 text-right">
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 px-2 text-xs text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                          onClick={() => { if (window.confirm(`Mark "${p.title}" as complete?`)) complete.mutate(p); }}
                          disabled={complete.isPending}
                          data-testid={`complete-${p.project_id}`}
                        >
                          <CheckCircle size={13} className="mr-1" /> Complete
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card-flat">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="font-display font-semibold text-slate-900">Financial snapshot</div>
            <div className="text-xs text-slate-500 mt-0.5">Paid vs outstanding</div>
          </div>
          <div className="p-5 space-y-5">
            <MiniStat icon={<TrendUp size={16} weight="duotone" />} label="Revenue (paid)" value={fmtMoney(data?.revenue || 0)} accent="emerald" />
            <MiniStat icon={<Clock size={16} weight="duotone" />} label="Outstanding" value={fmtMoney(data?.outstanding || 0)} accent="amber" />
            <MiniStat icon={<Briefcase size={16} weight="duotone" />} label="Completed projects" value={data?.completed_projects ?? 0} accent="blue" />
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon, label, value, tone = "slate", testid }) {
  const tones = {
    blue: "text-blue-800 bg-blue-50 border-blue-100",
    slate: "text-slate-700 bg-slate-50 border-slate-200",
    amber: "text-amber-800 bg-amber-50 border-amber-100",
  };
  return (
    <div className="card-flat p-5 hover-lift" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
        <div className={`w-8 h-8 rounded-md border flex items-center justify-center ${tones[tone]}`}>{icon}</div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-slate-900 tracking-tight">{value}</div>
    </div>
  );
}

function MiniStat({ icon, label, value, accent = "slate" }) {
  const map = { emerald: "text-emerald-700", amber: "text-amber-700", blue: "text-blue-800", slate: "text-slate-700" };
  return (
    <div className="flex items-center justify-between">
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wider ${map[accent]}`}>{icon}{label}</div>
      <div className="font-mono text-slate-900">{value}</div>
    </div>
  );
}

function ActionCard({ tone, icon, title, subtitle, to, testid, children }) {
  const tones = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <Link to={to} className={`card-flat p-5 hover-lift ${tones[tone]} block`} data-testid={testid}>
      <div className="flex items-center gap-2 font-display font-semibold">
        {icon}{title}
      </div>
      <div className="text-xs mt-1 opacity-80">{subtitle}</div>
      {children}
      <div className="text-[11px] uppercase tracking-[0.15em] mt-3 font-semibold opacity-70">Review →</div>
    </Link>
  );
}
